// Pull the entire live screen back, then bring the next chapter forward.
// Cached geometry and compositor transforms keep the strong zoom inexpensive.
export function createFomoSectionTransition({
  frame,
  sticky,
  incoming,
  header,
  isStill,
}) {
  const clamp = (x) => Math.max(0, Math.min(1, x));
  const ease = (x) => x * x * (3 - 2 * x);
  let chapter = -1;
  let geometry = {};
  let lastSweep = -1;
  const poses = new WeakMap();
  const flight = incoming.querySelector(".benefit-phone-flight");
  const phone = incoming.querySelector(".benefit-phone");
  const revealNodes = [
    ...incoming.querySelectorAll(
      ".benefit-top,.benefit-copy-stack,.benefit-nav,.checkout-trail,[data-benefit-panel='0'] .benefit-control"
    ),
  ];
  function measure() {
    // Offset geometry ignores the current scroll transforms, including when
    // resizing midway through the handoff or returning from a later section.
    let phoneCenter = 0;
    for (let node = phone; node && node !== incoming; node = node.offsetParent)
      phoneCenter += node.offsetLeft;
    geometry = {
      top: incoming.parentElement.getBoundingClientRect().top + scrollY,
      storyEnd: sticky.parentElement.getBoundingClientRect().bottom + scrollY,
      header: header.offsetHeight,
      height: sticky.clientHeight,
      viewport: innerHeight,
      width: incoming.clientWidth,
      phoneCenter: phoneCenter + 80,
      compact: innerWidth <= 600,
    };
    lastSweep = -1;
  }
  function prepare(next) {
    chapter = next;
  }

  function pose(node, scale, y, opacity = 1, x = 0) {
    const transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(
      1
    )}px,0) scale(${scale.toFixed(4)})`;
    const key = `${transform}|${opacity.toFixed(3)}`;
    if (poses.get(node) === key) return;
    poses.set(node, key);
    node.style.transform = transform;
    node.style.opacity = opacity.toFixed(3);
    node.classList.toggle("is-framed", scale < 0.999);
  }

  function resetSweep() {
    if (lastSweep === -2) return;
    lastSweep = -2;
    pose(sticky, 1, 0);
    sticky.style.visibility = "";
    pose(incoming, 1, 0);
    incoming.classList.remove("is-sweeping");
    incoming.inert = false;
    pose(flight, 1, 0);
    revealNodes.forEach((node) => pose(node, 1, 0));
  }

  function update(phase) {
    if (isStill()) {
      pose(frame, 1, 0);
      resetSweep();
      return;
    }
    const entry = chapter > 0 ? 1 - ease(clamp(phase / 0.18)) : 0;
    const exit = ease(clamp((phase - 0.82) / 0.18));
    const contraction = geometry.compact ? 0.46 : 0.54;
    if (chapter < 3) {
      // Keep the same small-screen position on both sides of a chapter change.
      // Only the content changes at the midpoint; the camera never jumps.
      pose(
        frame,
        1 - (entry + exit) * contraction,
        -(entry + exit) * geometry.height * 0.08
      );
      resetSweep();
      return;
    }

    // Mobile keeps the native vertical handoff. Counter-translating two sticky
    // screens fights touch scrolling (and the browser toolbar's resize).
    // Only the chapter's existing entrance zoom runs; the next surface remains
    // in normal flow and is immediately readable and interactive.
    if (geometry.compact) {
      pose(frame, 1 - entry * contraction, -geometry.height * entry * 0.08);
      resetSweep();
      return;
    }

    // On desktop QR finishes at full size, then benefits sweeps horizontally.
    // The real sticky sections keep their document flow and anchor positions.
    const trackTop = geometry.top - scrollY;
    const end = geometry.top - geometry.header;
    const distance = geometry.viewport;
    const progress = clamp((scrollY - end + distance) / distance);
    const sweep = ease(progress);
    pose(
      frame,
      1 - entry * contraction,
      -geometry.height * entry * 0.08,
      1,
      -geometry.width * sweep * 0.06
    );
    // Compensate the outgoing sticky's natural release until it is covered.
    pose(
      sticky,
      1,
      Math.min(
        geometry.height,
        Math.max(
          0,
          geometry.header + geometry.height - (geometry.storyEnd - scrollY)
        )
      )
    );
    sticky.style.visibility = progress === 1 ? "hidden" : "";
    if (!progress) {
      // Stage the whole screen beyond the right edge before the short sweep
      // starts. Leaving it in normal flow lets it rise into view first.
      pose(
        incoming,
        1,
        geometry.header - Math.max(geometry.header, trackTop),
        1,
        geometry.width
      );
      if (lastSweep !== 0) {
        incoming.classList.remove("is-sweeping");
        incoming.inert = true;
        pose(flight, 1, 0);
        revealNodes.forEach((node) => pose(node, 1, 0));
        lastSweep = 0;
      }
      return;
    }
    pose(
      incoming,
      1,
      geometry.header - Math.max(geometry.header, trackTop),
      1,
      geometry.width * (1 - sweep)
    );
    if (lastSweep === progress) return;
    lastSweep = progress;
    incoming.classList.toggle("is-sweeping", progress < 1);
    incoming.inert = progress < 0.98;
    // The live phone crosses the leading edge, then settles into its existing
    // checkout animation. Its wrapper leaves that animation's transform alone.
    const lead = 1 - ease(clamp((progress - 0.42) / 0.58));
    const peek = 42;
    pose(
      flight,
      1,
      0,
      ease(clamp(progress / 0.12)),
      -(geometry.phoneCenter - peek) * lead
    );
    const reveal = ease(clamp((progress - 0.62) / 0.36));
    revealNodes.forEach((node) => pose(node, 1, (1 - reveal) * 22, reveal));
  }

  return {
    prepare,
    measure,
    update,
    destroy() {
      pose(frame, 1, 0);
      resetSweep();
    },
  };
}
